import { useMemo } from 'react';
import {
  ContentGenerationMetricType,
  KeywordSection
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { useGetContentGenerationMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { formatMetricPair } from './reviewUtils';

type ResolvedContent = {
  title?: string;
  description?: string;
  bullets?: string[];
  itemHighlights?: string;
};

type MetricChange = { absolute: string; percentage: string };
type MetricDisplay = {
  contentUtilization: string | number;
  keywordsUsed: number;
  productiveKeywords: number;
  keywordPopularity: string | number;
};
type MetricDisplayWithChanges = MetricDisplay & {
  changes: {
    contentUtilization: MetricChange;
    keywordsUsed: MetricChange;
    productiveKeywords: MetricChange;
    keywordPopularity: MetricChange;
  };
};

export type ComparisonMetrics = {
  leftMetrics: MetricDisplay;
  rightMetrics: MetricDisplayWithChanges;
  metricLoadingStates: {
    leftUtilization: boolean;
    leftKeywordUsage: boolean;
    leftKeywordProductivity: boolean;
    leftKeywordPopularity: boolean;
    rightUtilization: boolean;
    rightKeywordUsage: boolean;
    rightKeywordProductivity: boolean;
    rightKeywordPopularity: boolean;
  };
};

export const useComparisonMetrics = (params: {
  retailerSku: string;
  retailerId: number;
  weekId: number;
  stacklineSku: string;
  profileId?: string;
  profileVersion?: number;
  leftResolved?: ResolvedContent;
  rightResolved?: ResolvedContent;
  leftSections?: KeywordSection[];
  rightSections?: KeywordSection[];
  enabledLeft: boolean;
  enabledRight: boolean;
}): ComparisonMetrics => {
  const {
    retailerSku,
    retailerId,
    weekId,
    stacklineSku,
    profileId,
    profileVersion,
    leftResolved,
    rightResolved,
    leftSections,
    rightSections,
    enabledLeft,
    enabledRight
  } = params;

  // Left metrics
  const { metrics: leftUtilization, isLoading: isLoadingLeftUtilization } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: leftResolved?.title,
      description: leftResolved?.description,
      bullets: leftResolved?.bullets,
      itemHighlights: leftResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.ContentUtilizationScore
    },
    enabledLeft
  );

  const { metrics: leftKeywordUsage, isLoading: isLoadingLeftKeywordUsage } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: leftResolved?.title,
      description: leftResolved?.description,
      bullets: leftResolved?.bullets,
      itemHighlights: leftResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.KeywordUsageScore
    },
    enabledLeft
  );

  const { metrics: leftKeywordProductivity, isLoading: isLoadingLeftKeywordProductivity } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: leftResolved?.title,
      description: leftResolved?.description,
      bullets: leftResolved?.bullets,
      itemHighlights: leftResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.KeywordProductivityScore
    },
    enabledLeft
  );

  const { metrics: leftKeywordPopularity, isLoading: isLoadingLeftKeywordPopularity } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: leftResolved?.title,
      description: leftResolved?.description,
      bullets: leftResolved?.bullets,
      itemHighlights: leftResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.KeywordPopularityScore
    },
    enabledLeft
  );

  // Right metrics
  const { metrics: rightUtilization, isLoading: isLoadingRightUtilization } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: rightResolved?.title,
      description: rightResolved?.description,
      bullets: rightResolved?.bullets,
      itemHighlights: rightResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.ContentUtilizationScore
    },
    enabledRight
  );

  const { metrics: rightKeywordUsage, isLoading: isLoadingRightKeywordUsage } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: rightResolved?.title,
      description: rightResolved?.description,
      bullets: rightResolved?.bullets,
      itemHighlights: rightResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.KeywordUsageScore
    },
    enabledRight
  );

  const { metrics: rightKeywordProductivity, isLoading: isLoadingRightKeywordProductivity } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: rightResolved?.title,
      description: rightResolved?.description,
      bullets: rightResolved?.bullets,
      itemHighlights: rightResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.KeywordProductivityScore
    },
    enabledRight
  );

  const { metrics: rightKeywordPopularity, isLoading: isLoadingRightKeywordPopularity } = useGetContentGenerationMetrics(
    {
      retailerSku,
      retailerId,
      weekId,
      stacklineSku,
      profileId,
      profileVersion,
      title: rightResolved?.title,
      description: rightResolved?.description,
      bullets: rightResolved?.bullets,
      itemHighlights: rightResolved?.itemHighlights,
      metricType: ContentGenerationMetricType.KeywordPopularityScore
    },
    enabledRight
  );

  // Compose loading states
  const metricLoadingStates = {
    leftUtilization: isLoadingLeftUtilization,
    leftKeywordUsage: isLoadingLeftKeywordUsage,
    leftKeywordProductivity: isLoadingLeftKeywordProductivity,
    leftKeywordPopularity: isLoadingLeftKeywordPopularity,
    rightUtilization: isLoadingRightUtilization,
    rightKeywordUsage: isLoadingRightKeywordUsage,
    rightKeywordProductivity: isLoadingRightKeywordProductivity,
    rightKeywordPopularity: isLoadingRightKeywordPopularity
  } as const;

  // Compose metric pairs
  const utilizationPair = useMemo(() => {
    const leftPercent = (leftUtilization?.score ?? 0) * 100;
    const rightPercent = (rightUtilization?.score ?? 0) * 100;
    return formatMetricPair('utilization', leftPercent, rightPercent);
  }, [leftUtilization, rightUtilization]);

  const keywordUsagePair = useMemo(() => {
    const leftCount = leftKeywordUsage?.averageCountOfKeywords ?? 0;
    const rightCount = rightKeywordUsage?.averageCountOfKeywords ?? 0;
    return formatMetricPair('keywordUsage', leftCount, rightCount);
  }, [leftKeywordUsage, rightKeywordUsage]);

  const productivityPair = useMemo(() => {
    const leftCount = leftKeywordProductivity?.averageCountOfKeywordsGeneratingTraffic ?? 0;
    const rightCount = rightKeywordProductivity?.averageCountOfKeywordsGeneratingTraffic ?? 0;
    return formatMetricPair('productivity', leftCount, rightCount);
  }, [leftKeywordProductivity, rightKeywordProductivity]);

  const popularityPair = useMemo(() => {
    const leftPercent = (leftKeywordPopularity?.score ?? 0) * 100;
    const rightPercent = (rightKeywordPopularity?.score ?? 0) * 100;
    return formatMetricPair('popularity', leftPercent, rightPercent);
  }, [leftKeywordPopularity, rightKeywordPopularity]);

  const leftMetrics: MetricDisplay = useMemo(
    () => ({
      contentUtilization: utilizationPair.current,
      keywordsUsed: Number(keywordUsagePair.current),
      productiveKeywords: Number(productivityPair.current),
      keywordPopularity: popularityPair.current
    }),
    [utilizationPair, keywordUsagePair, productivityPair, popularityPair]
  );

  const rightMetrics: MetricDisplayWithChanges = useMemo(
    () => ({
      contentUtilization: utilizationPair.new,
      keywordsUsed: Number(keywordUsagePair.new),
      productiveKeywords: Number(productivityPair.new),
      keywordPopularity: popularityPair.new,
      changes: {
        contentUtilization: utilizationPair.change,
        keywordsUsed: keywordUsagePair.change,
        productiveKeywords: productivityPair.change,
        keywordPopularity: popularityPair.change
      }
    }),
    [utilizationPair, keywordUsagePair, productivityPair, popularityPair]
  );

  return {
    leftMetrics,
    rightMetrics,
    metricLoadingStates
  };
};


